import { useEffect, useState } from 'react';
import { Calendar, Download, ExternalLink } from 'lucide-react';
import { BottomSheet } from './Overlay';
import api from '../lib/api';

/**
 * AddToCalendarSheet — Q32 Phase A.
 *
 * Opens from PlanDetails. Offers four targets:
 *   • Apple Calendar (.ics download)
 *   • Google Calendar (web pre-fill)
 *   • Outlook (web pre-fill)
 *   • Download .ics (generic)
 */
export default function AddToCalendarSheet({ open, onClose, planId }) {
  const [links, setLinks] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !planId) return;
    setLoading(true);
    api.get(`/calendar/links/${planId}`)
      .then(r => setLinks(r.data))
      .catch(() => setLinks(null))
      .finally(() => setLoading(false));
  }, [open, planId]);

  const downloadIcs = async () => {
    try {
      const res = await api.get(`/calendar/ics/plan/${planId}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/calendar' }));
      const a = document.createElement('a');
      a.href = url; a.download = 'asap-plan.ics';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {}
  };

  return (
    <BottomSheet open={open} onClose={onClose} title="Add to calendar">
      <div data-testid="add-to-calendar-sheet" className="space-y-2">
        {loading && <p className="text-xs text-[#9B9EA3] text-center py-2">Loading…</p>}
        <Row
          testid="add-to-cal-apple"
          icon={<Download size={16} />}
          title="Apple Calendar"
          subtitle="Saves an .ics file iOS opens automatically"
          onClick={downloadIcs}
        />
        <Row
          testid="add-to-cal-google"
          icon={<ExternalLink size={16} />}
          title="Google Calendar"
          subtitle="Opens a pre-filled add-event page"
          disabled={!links?.google}
          onClick={() => links?.google && window.open(links.google, '_blank', 'noopener')}
        />
        <Row
          testid="add-to-cal-outlook"
          icon={<ExternalLink size={16} />}
          title="Outlook"
          subtitle="Outlook.com personal calendar"
          disabled={!links?.outlook}
          onClick={() => links?.outlook && window.open(links.outlook, '_blank', 'noopener')}
        />
        <Row
          testid="add-to-cal-ics"
          icon={<Download size={16} />}
          title="Download .ics"
          subtitle="Works with any calendar app"
          onClick={downloadIcs}
        />
        <p className="text-[10px] text-[#9B9EA3] text-center pt-2 leading-relaxed">
          Pro tip — recurring plans carry RRULE so they repeat automatically in your calendar.
        </p>
      </div>
    </BottomSheet>
  );
}

function Row({ testid, icon, title, subtitle, onClick, disabled }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      className="w-full flex items-center gap-3 bg-white rounded-2xl border border-black/[0.06] p-3 active:bg-[#F1F0EA] disabled:opacity-40"
    >
      <div className="w-9 h-9 rounded-2xl bg-[#B6E600]/15 text-[#3F5200] flex items-center justify-center">
        {icon}
      </div>
      <div className="flex-1 text-left">
        <p className="text-sm font-bold text-[#111]">{title}</p>
        <p className="text-[11px] text-[#9B9EA3]">{subtitle}</p>
      </div>
    </button>
  );
}
